import { scopedThreadKey, scopeProjectRef } from "@cmmd-forge/client-runtime";
import type {
  LinkedPullRequest,
  PullRequestActivity,
  PullRequestDetail,
  PullRequestRef,
  ScopedProjectRef,
} from "@cmmd-forge/contracts";
import { useState } from "react";

import { useComposerDraftStore } from "~/composerDraftStore";
import { toastManager } from "~/components/ui/toast";
import { ensureEnvironmentApi } from "~/environmentApi";
import { newCommandId } from "~/lib/utils";
import { useHandleNewThread, useNewThreadHandler } from "~/hooks/useHandleNewThread";
import type { Project } from "~/types";
import { draftRightPanelKey, useRightPanelStore } from "~/rightPanelStore";
import type { RightPanelSurface } from "~/rightPanelSurface";
import type { PullRequestAgentSelection } from "../pullRequestSelectionContext";
import {
  executePullRequestCheckoutTransaction,
  type PullRequestHandoffAction,
  type PullRequestHandoffFinding,
  replaceGeneratedHandoffPrompt,
} from "./pullRequestHandoffPlanning";
import { buildPullRequestHandoffPrompt } from "./pullRequestHandoffPrompt";

export function usePullRequestHandoff(input: {
  readonly detail: PullRequestDetail;
  readonly reference: PullRequestRef;
  readonly activity: PullRequestActivity | undefined;
  readonly sourceProject: Project;
  readonly targetProject: Project;
  readonly selection: PullRequestAgentSelection | null;
  readonly canLinkPullRequest: boolean;
}) {
  const [pendingAction, setPendingAction] = useState<PullRequestHandoffAction | null>(null);
  const thread = useHandleNewThread();
  const { openNewThread } = useNewThreadHandler();
  const ownsPullRequest = useRightPanelStore((state) =>
    activeComposerOwnsPullRequest(
      state.byThreadKey,
      thread.activeComposerTarget,
      input.detail,
      input.sourceProject.environmentId,
    ),
  );
  const currentTarget = matchingComposerTarget(input.targetProject, thread, ownsPullRequest);

  const run = async (
    action: PullRequestHandoffAction,
    targetProject = input.targetProject,
    finding?: PullRequestHandoffFinding,
  ) => {
    if (pendingAction !== null) return;
    setPendingAction(action);
    try {
      await runPullRequestHandoff({
        ...input,
        targetProject,
        action,
        ...(finding === undefined ? {} : { finding }),
        currentTarget,
        thread,
        openNewThread,
        canLinkPullRequest: input.canLinkPullRequest,
      });
      showHandoffSuccess(action);
    } catch (cause) {
      showHandoffFailure(cause);
    } finally {
      setPendingAction(null);
    }
  };

  return { currentThreadMatches: currentTarget !== null, pendingAction, run };
}

type ThreadHook = ReturnType<typeof useHandleNewThread>;
type OpenNewThread = ReturnType<typeof useNewThreadHandler>["openNewThread"];
type ComposerTarget = NonNullable<ThreadHook["activeComposerTarget"]>;

async function runPullRequestHandoff(input: {
  readonly action: PullRequestHandoffAction;
  readonly detail: PullRequestDetail;
  readonly reference: PullRequestRef;
  readonly activity: PullRequestActivity | undefined;
  readonly sourceProject: Project;
  readonly targetProject: Project;
  readonly selection: PullRequestAgentSelection | null;
  readonly finding?: PullRequestHandoffFinding;
  readonly currentTarget: ComposerTarget | null;
  readonly thread: ThreadHook;
  readonly openNewThread: OpenNewThread;
  readonly canLinkPullRequest: boolean;
}) {
  const prompt = buildPullRequestHandoffPrompt({
    action: input.action,
    detail: input.detail,
    activity: input.activity,
    sourceEnvironmentId: input.sourceProject.environmentId,
    selection: input.selection,
    messages: input.thread.activeThread?.messages ?? [],
    ...(input.finding === undefined ? {} : { finding: input.finding }),
  });
  const projectRef = scopeProjectRef(input.targetProject.environmentId, input.targetProject.id);
  const linkedPullRequest = input.canLinkPullRequest
    ? linkedPullRequestFromDetail(input.detail, input.reference)
    : null;
  const execution = resolveHandoffExecution(input.action, input.currentTarget);
  await HANDOFF_EXECUTORS[execution.kind]({
    input,
    projectRef,
    execution,
    prompt,
    linkedPullRequest,
  });
}

type ExecutionContext = {
  readonly input: Parameters<typeof runPullRequestHandoff>[0];
  readonly projectRef: ScopedProjectRef;
  readonly execution: HandoffExecution;
  readonly prompt: string | null;
  readonly linkedPullRequest: LinkedPullRequest | null;
};

const HANDOFF_EXECUTORS: Readonly<
  Record<HandoffExecution["kind"], (context: ExecutionContext) => Promise<void>>
> = {
  current: async (context) => {
    const execution = context.execution as Extract<HandoffExecution, { kind: "current" }>;
    await persistCurrentThreadPullRequestLink(execution.target, context.linkedPullRequest);
    writeUnsentPrompt(execution.target, requirePrompt(context.prompt));
  },
  "new-without-checkout": async (context) => {
    const opened = await context.input.openNewThread(context.projectRef, {
      draftReuse: "never",
      ...(context.linkedPullRequest ? { linkedPullRequest: context.linkedPullRequest } : {}),
    });
    writeUnsentPrompt(opened.draftId, requirePrompt(context.prompt));
  },
  checkout: async (context) => {
    const execution = context.execution as Extract<HandoffExecution, { kind: "checkout" }>;
    await runCheckoutHandoff(
      context.input,
      context.projectRef,
      execution.checkout,
      context.prompt,
      context.linkedPullRequest,
    );
  },
};

async function runCheckoutHandoff(
  input: Parameters<typeof runPullRequestHandoff>[0],
  projectRef: ScopedProjectRef,
  checkout: "local" | "worktree",
  prompt: string | null,
  linkedPullRequest: LinkedPullRequest | null,
) {
  return executePullRequestCheckoutTransaction({
    openDraft: () =>
      input.openNewThread(projectRef, {
        draftReuse: "never",
        ...(linkedPullRequest ? { linkedPullRequest } : {}),
      }),
    prepareCheckout: (opened) =>
      ensureEnvironmentApi(projectRef.environmentId).git.preparePullRequestThread({
        cwd: input.targetProject.cwd,
        reference: input.detail.url,
        mode: checkout,
        threadId: opened.threadId,
      }),
    repointDraft: async (_opened, prepared) => {
      await input.openNewThread(projectRef, {
        branch: prepared.branch,
        worktreePath: prepared.worktreePath,
        envMode: prepared.worktreePath === null ? "local" : "worktree",
        ...(linkedPullRequest ? { linkedPullRequest } : {}),
      });
    },
    writeUnsent: prompt === null ? null : (opened) => writeUnsentPrompt(opened.draftId, prompt),
  });
}

function linkedPullRequestFromDetail(
  detail: PullRequestDetail,
  reference: PullRequestRef,
): LinkedPullRequest | null {
  if (reference.projectRepositoryId === undefined) return null;
  try {
    const host = new URL(detail.url).hostname;
    if (host.length === 0) return null;
    return {
      projectId: detail.projectId,
      projectRepositoryId: reference.projectRepositoryId,
      provider: detail.provider,
      host,
      repository: detail.repository,
      number: detail.number,
      url: detail.url,
    };
  } catch {
    return null;
  }
}

async function persistCurrentThreadPullRequestLink(
  target: ComposerTarget,
  linkedPullRequest: LinkedPullRequest | null,
): Promise<void> {
  if (linkedPullRequest === null) return;
  if (typeof target === "string") {
    useComposerDraftStore.getState().setDraftThreadContext(target, { linkedPullRequest });
    return;
  }
  await ensureEnvironmentApi(target.environmentId).orchestration.dispatchCommand({
    type: "thread.meta.update",
    commandId: newCommandId(),
    threadId: target.threadId,
    linkedPullRequest,
  });
}

type HandoffExecution =
  | { readonly kind: "current"; readonly target: ComposerTarget }
  | { readonly kind: "new-without-checkout" }
  | { readonly kind: "checkout"; readonly checkout: "local" | "worktree" };

function resolveHandoffExecution(
  action: PullRequestHandoffAction,
  currentTarget: ComposerTarget | null,
): HandoffExecution {
  if (currentTarget !== null && CURRENT_THREAD_ACTIONS.has(action)) {
    return { kind: "current", target: currentTarget };
  }
  return NEW_THREAD_EXECUTION[action];
}

const CURRENT_THREAD_ACTIONS = new Set<PullRequestHandoffAction>([
  "ask",
  "explain",
  "fix",
  "fix-finding",
  "resolve-conflicts",
  "selection",
]);
const NEW_THREAD_EXECUTION: Readonly<Record<PullRequestHandoffAction, HandoffExecution>> = {
  ask: { kind: "new-without-checkout" },
  explain: { kind: "new-without-checkout" },
  selection: { kind: "new-without-checkout" },
  fix: { kind: "checkout", checkout: "worktree" },
  "fix-new": { kind: "checkout", checkout: "worktree" },
  "fix-finding": { kind: "checkout", checkout: "worktree" },
  "resolve-conflicts": { kind: "checkout", checkout: "worktree" },
  transfer: { kind: "checkout", checkout: "worktree" },
  "checkout-local": { kind: "checkout", checkout: "local" },
  "checkout-worktree": { kind: "checkout", checkout: "worktree" },
};

function matchingComposerTarget(
  project: Project,
  thread: ThreadHook,
  ownsPullRequest: boolean,
): ComposerTarget | null {
  if (!ownsPullRequest) return null;
  if (thread.activeComposerTarget === null) return null;
  const activeProject = activeThreadProject(thread);
  if (activeProject === null) return null;
  return scopedProjectMatches(activeProject, project) ? thread.activeComposerTarget : null;
}

function activeComposerOwnsPullRequest(
  byThreadKey: ReturnType<typeof useRightPanelStore.getState>["byThreadKey"],
  target: ComposerTarget | null,
  detail: PullRequestDetail,
  environmentId: Project["environmentId"],
) {
  if (target === null) return false;
  const key = typeof target === "string" ? draftRightPanelKey(target) : scopedThreadKey(target);
  return (byThreadKey[key]?.surfaces ?? []).some((surface) =>
    pullRequestSurfaceMatches(surface, detail, environmentId),
  );
}

export function pullRequestSurfaceMatches(
  surface: RightPanelSurface,
  detail: Pick<PullRequestDetail, "projectId" | "repository" | "number">,
  environmentId: Project["environmentId"],
) {
  return (
    surface.kind === "pull-request" &&
    surface.environmentId === environmentId &&
    surface.projectId === detail.projectId &&
    surface.repository === detail.repository &&
    surface.number === detail.number
  );
}

function scopedProjectMatches(reference: ScopedProjectRef, project: Project) {
  return reference.projectId === project.id && reference.environmentId === project.environmentId;
}

function activeThreadProject(thread: ThreadHook): ScopedProjectRef | null {
  if (thread.activeThread) {
    return scopeProjectRef(thread.activeThread.environmentId, thread.activeThread.projectId);
  }
  if (thread.activeDraftThread !== null) {
    return scopeProjectRef(
      thread.activeDraftThread.environmentId,
      thread.activeDraftThread.projectId,
    );
  }
  return null;
}

function writeUnsentPrompt(target: ComposerTarget, generatedPrompt: string) {
  const store = useComposerDraftStore.getState();
  const current = store.getComposerDraft(target)?.prompt ?? "";
  const next = replaceGeneratedHandoffPrompt(
    current,
    store.getComposerDraft(target)?.lastGeneratedPullRequestPrompt,
    generatedPrompt,
  );
  store.setPrompt(target, next);
  store.setGeneratedPullRequestPrompt(target, generatedPrompt);
  store.setPendingAutoSubmit(target, false);
}

const CHECKOUT_ONLY_ACTIONS = new Set<PullRequestHandoffAction>([
  "checkout-local",
  "checkout-worktree",
]);

function requirePrompt(prompt: string | null) {
  if (prompt === null) throw new Error("This pull request action has no composer message.");
  return prompt;
}

function showHandoffSuccess(action: PullRequestHandoffAction) {
  const checkoutOnly = CHECKOUT_ONLY_ACTIONS.has(action);
  toastManager.add({
    type: "success",
    title: checkoutOnly ? "Checkout ready" : "Added to the composer",
    description: checkoutOnly
      ? "The pull request checkout is ready in its thread."
      : "Review the generated message, then send it when ready.",
  });
}

function showHandoffFailure(cause: unknown) {
  toastManager.add({
    type: "error",
    title: "Pull request handoff failed",
    description: cause instanceof Error ? cause.message : "Try the handoff again.",
  });
}
